import React, { useState, useEffect, useCallback } from 'react';
import { Activity, ChevronDown, ChevronUp, Info, Database, Link as LinkIcon, Settings, Loader2, AlertCircle, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import GlassPanel from '../../components/GlassPanel';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Business Reality Dashboard
 * 
 * READ-ONLY. Advisory only. Calm, factual UI.
 * 
 * NO alerts. NO colours implying success/failure.
 * NO calls to action. NO AI interaction.
 */

// Burn State Badge - Phase 1D
function BurnStateBadge({ burnRateInfo }) {
  if (!burnRateInfo) return null;
  
  const { burn_rate_state, is_provisional, burn_rate_variance_pct } = burnRateInfo;
  
  const stateLabels = {
    MANUAL: { label: 'MANUAL', subtext: 'Provisional — connect Xero + Bank to verify.' },
    HYBRID: { 
      label: 'HYBRID', 
      subtext: burn_rate_variance_pct !== null 
        ? `Partial data — variance: ${burn_rate_variance_pct.toFixed(1)}% vs manual.`
        : 'Partial data — limited months available.'
    },
    VERIFIED: { label: 'VERIFIED', subtext: 'Verified from linked finance data.' }
  };
  
  const state = stateLabels[burn_rate_state] || stateLabels.MANUAL;
  
  return (
    <div 
      style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'flex-end',
        gap: '4px'
      }}
      data-testid="burn-state-badge"
    >
      <span 
        style={{
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '0.6875rem',
          fontWeight: '600',
          letterSpacing: '0.04em',
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border-primary)',
          color: 'var(--text-primary)'
        }}
        data-testid="burn-state-label"
      >
        Burn rate: {state.label}
      </span>
      <span 
        style={{ 
          fontSize: '0.6875rem', 
          color: 'var(--text-muted)',
          textAlign: 'right',
          maxWidth: '200px'
        }}
        data-testid="burn-state-subtext"
      >
        {state.subtext}
      </span>
      {is_provisional && (
        <span 
          style={{ 
            fontSize: '0.625rem', 
            color: 'var(--text-muted)',
            fontStyle: 'italic'
          }}
          data-testid="provisional-indicator"
        >
          Targets shown as provisional.
        </span>
      )}
    </div>
  );
}

// Stability Buffer Card - Phase 1D
function StabilityBufferCard({ stability }) {
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  
  if (!stability) return null;
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  
  const formatMonths = (value) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(1);
  };
  
  // Blocked State
  if (!stability.is_available) {
    return (
      <GlassPanel 
        className="mb-lg" 
        title="Stability Buffer"
        data-testid="stability-buffer-card"
      >
        <div style={{ padding: 'var(--space-md) 0' }}>
          <p 
            style={{ 
              color: 'var(--text-muted)', 
              fontSize: '0.875rem',
              lineHeight: 1.6,
              marginBottom: 'var(--space-lg)'
            }}
            data-testid="stability-blocked-reason"
          >
            {stability.blocked_reason}
          </p>
          <Link 
            to="/finance/xero"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-primary)',
              fontSize: '0.875rem',
              fontWeight: '500',
              textDecoration: 'none',
              transition: 'background 0.2s ease'
            }}
            data-testid="connect-integrations-btn"
          >
            <LinkIcon size={14} />
            Connect Xero / Bank
          </Link>
        </div>
      </GlassPanel>
    );
  }
  
  // Available State
  return (
    <GlassPanel 
      className="mb-lg" 
      title="Stability Buffer"
      data-testid="stability-buffer-card"
    >
      <div style={{ padding: 'var(--space-md) 0' }}>
        {/* Primary Values */}
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <div 
            style={{ 
              fontSize: '2rem', 
              fontWeight: '600', 
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family-display)',
              marginBottom: 'var(--space-xs)'
            }}
            data-testid="stability-months"
          >
            Stability: {formatMonths(stability.stability_months)} months
          </div>
          <div 
            style={{ 
              fontSize: '1rem', 
              color: 'var(--text-muted)',
              marginBottom: 'var(--space-xs)'
            }}
            data-testid="available-cash"
          >
            Available cash: {formatCurrency(stability.available_cash)}
          </div>
          <div 
            style={{ 
              fontSize: '1rem', 
              color: 'var(--text-muted)'
            }}
            data-testid="six-month-target"
          >
            6-month target: {formatCurrency(stability.six_month_target)}
          </div>
        </div>
        
        {/* Breakdown Accordion */}
        <div 
          style={{
            borderTop: '1px solid var(--border-primary)',
            paddingTop: 'var(--space-md)'
          }}
        >
          <button
            onClick={() => setBreakdownExpanded(!breakdownExpanded)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-sm) 0',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              fontSize: '0.875rem'
            }}
            data-testid="breakdown-toggle"
          >
            <span>Breakdown</span>
            {breakdownExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          
          {breakdownExpanded && (
            <div 
              style={{ 
                paddingTop: 'var(--space-sm)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-sm)'
              }}
              data-testid="stability-breakdown"
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Operating balance</span>
                <span style={{ color: 'var(--text-primary)' }} data-testid="operating-balance">
                  {formatCurrency(stability.operating_balance)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>VAT owed</span>
                <span style={{ color: 'var(--text-primary)' }} data-testid="vat-owed">
                  {formatCurrency(stability.vat_owed)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Committed costs</span>
                <span style={{ color: 'var(--text-primary)' }} data-testid="committed-costs">
                  {formatCurrency(stability.committed_costs_total)}
                </span>
              </div>
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  fontSize: '0.875rem',
                  paddingTop: 'var(--space-sm)',
                  borderTop: '1px solid var(--border-primary)',
                  fontWeight: '500'
                }}
              >
                <span style={{ color: 'var(--text-muted)' }}>Buffer gap</span>
                <span 
                  style={{ color: 'var(--text-primary)' }} 
                  data-testid="buffer-gap"
                >
                  {stability.buffer_gap >= 0 ? '+' : ''}{formatCurrency(stability.buffer_gap)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </GlassPanel>
  );
}

// Confirmation Modal - Phase 1E
function ConfirmationModal({ isOpen, onCancel, onConfirm, saving }) {
  if (!isOpen) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      data-testid="confirmation-modal-overlay"
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-primary)',
          padding: 'var(--space-xl)',
          maxWidth: '480px',
          width: '90%'
        }}
        data-testid="confirmation-modal"
      >
        <h3 style={{ 
          fontSize: '1.125rem', 
          fontWeight: '600', 
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-md)'
        }}>
          Confirm Financial Target Change
        </h3>
        
        <p style={{ 
          fontSize: '0.875rem', 
          color: 'var(--text-muted)',
          lineHeight: 1.6,
          marginBottom: 'var(--space-md)'
        }}>
          Changing income or burn targets will immediately affect:
        </p>
        
        <ul style={{ 
          fontSize: '0.875rem', 
          color: 'var(--text-muted)',
          lineHeight: 1.8,
          marginBottom: 'var(--space-lg)',
          paddingLeft: 'var(--space-lg)'
        }}>
          <li>Required profit per job</li>
          <li>Quote baseline comparisons</li>
          <li>Business reality projections</li>
        </ul>
        
        <p style={{ 
          fontSize: '0.875rem', 
          color: 'var(--text-muted)',
          marginBottom: 'var(--space-xl)'
        }}>
          This may alter pricing guidance across your system.
        </p>
        
        <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: 'transparent',
              border: '1px solid var(--border-primary)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--text-muted)',
              fontSize: '0.875rem',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.5 : 1
            }}
            data-testid="modal-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: 'var(--accent-primary)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            data-testid="modal-confirm-btn"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Check size={14} />
                Confirm & Apply
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Income & Burn Targets Configuration - Phase 1E
function IncomeTargetsConfig({ 
  incomeInputs, 
  burnRateInfo, 
  onSave, 
  saving 
}) {
  const [formData, setFormData] = useState({
    personal_minimum_monthly: '',
    personal_target_monthly: '',
    business_burn_rate_manual: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form with existing data
  useEffect(() => {
    if (incomeInputs) {
      setFormData({
        personal_minimum_monthly: incomeInputs.personal_minimum_monthly ?? '',
        personal_target_monthly: incomeInputs.personal_target_monthly ?? '',
        business_burn_rate_manual: incomeInputs.business_burn_rate_manual ?? ''
      });
    }
  }, [incomeInputs]);

  const isBurnVerified = burnRateInfo?.burn_rate_state === 'VERIFIED';

  const handleInputChange = (field, value) => {
    const numValue = value === '' ? '' : parseFloat(value);
    setFormData(prev => ({ ...prev, [field]: numValue }));
    setHasChanges(true);
    
    // Clear validation error on change
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const min = parseFloat(formData.personal_minimum_monthly) || 0;
    const target = parseFloat(formData.personal_target_monthly) || 0;
    const burn = parseFloat(formData.business_burn_rate_manual) || 0;

    // All values >= 0
    if (formData.personal_minimum_monthly !== '' && min < 0) {
      errors.personal_minimum_monthly = 'Must be 0 or greater';
    }
    if (formData.personal_target_monthly !== '' && target < 0) {
      errors.personal_target_monthly = 'Must be 0 or greater';
    }
    if (formData.business_burn_rate_manual !== '' && burn < 0) {
      errors.business_burn_rate_manual = 'Must be 0 or greater';
    }

    // minimum > 0 (if set)
    if (formData.personal_minimum_monthly !== '' && min <= 0) {
      errors.personal_minimum_monthly = 'Minimum must be greater than 0';
    }

    // target >= minimum
    if (formData.personal_minimum_monthly !== '' && formData.personal_target_monthly !== '') {
      if (target < min) {
        errors.personal_target_monthly = 'Target must be at least the minimum';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = () => {
    if (!validateForm()) return;
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    const payload = {};
    
    if (formData.personal_minimum_monthly !== '') {
      payload.personal_minimum_monthly = parseFloat(formData.personal_minimum_monthly);
    }
    if (formData.personal_target_monthly !== '') {
      payload.personal_target_monthly = parseFloat(formData.personal_target_monthly);
    }
    // Only include burn rate if not verified
    if (!isBurnVerified && formData.business_burn_rate_manual !== '') {
      payload.business_burn_rate_manual = parseFloat(formData.business_burn_rate_manual);
    }
    
    const success = await onSave(payload);
    if (success) {
      setShowConfirmModal(false);
      setHasChanges(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    return value;
  };

  return (
    <>
      <GlassPanel 
        title="Income & Burn Targets" 
        titleIcon={Settings}
        className="mb-lg"
        data-testid="income-targets-section"
      >
        <div style={{ padding: 'var(--space-md) 0' }}>
          <div className="responsive-grid-3" style={{ 
            gap: 'var(--space-lg)',
            marginBottom: 'var(--space-lg)'
          }}>
            {/* Personal Minimum Monthly */}
            <div>
              <label 
                style={{ 
                  display: 'block',
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 'var(--space-xs)'
                }}
              >
                Minimum Monthly Income
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem'
                }}>£</span>
                <input
                  type="number"
                  value={formatCurrency(formData.personal_minimum_monthly)}
                  onChange={(e) => handleInputChange('personal_minimum_monthly', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="100"
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 28px',
                    background: 'var(--bg-tertiary)',
                    border: validationErrors.personal_minimum_monthly 
                      ? '1px solid var(--status-error)' 
                      : '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9375rem'
                  }}
                  data-testid="input-minimum-income"
                />
              </div>
              {validationErrors.personal_minimum_monthly && (
                <span style={{ fontSize: '0.75rem', color: 'var(--status-error)', marginTop: '4px', display: 'block' }}>
                  {validationErrors.personal_minimum_monthly}
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                Needed to cover personal expenses
              </span>
            </div>

            {/* Personal Target Monthly */}
            <div>
              <label 
                style={{ 
                  display: 'block',
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 'var(--space-xs)'
                }}
              >
                Target Monthly Income
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem'
                }}>£</span>
                <input
                  type="number"
                  value={formatCurrency(formData.personal_target_monthly)}
                  onChange={(e) => handleInputChange('personal_target_monthly', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="100"
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 28px',
                    background: 'var(--bg-tertiary)',
                    border: validationErrors.personal_target_monthly 
                      ? '1px solid var(--status-error)' 
                      : '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--text-primary)',
                    fontSize: '0.9375rem'
                  }}
                  data-testid="input-target-income"
                />
              </div>
              {validationErrors.personal_target_monthly && (
                <span style={{ fontSize: '0.75rem', color: 'var(--status-error)', marginTop: '4px', display: 'block' }}>
                  {validationErrors.personal_target_monthly}
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                Your desired monthly take-home
              </span>
            </div>

            {/* Business Burn Rate Manual */}
            <div>
              <label 
                style={{ 
                  display: 'block',
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 'var(--space-xs)'
                }}
              >
                Monthly Burn Rate
              </label>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  fontSize: '0.875rem'
                }}>£</span>
                <input
                  type="number"
                  value={isBurnVerified 
                    ? (burnRateInfo?.burn_rate_value || '') 
                    : formatCurrency(formData.business_burn_rate_manual)
                  }
                  onChange={(e) => handleInputChange('business_burn_rate_manual', e.target.value)}
                  placeholder="0"
                  min="0"
                  step="100"
                  disabled={isBurnVerified}
                  style={{
                    width: '100%',
                    padding: '10px 12px 10px 28px',
                    background: isBurnVerified ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                    border: validationErrors.business_burn_rate_manual 
                      ? '1px solid var(--status-error)' 
                      : '1px solid var(--border-primary)',
                    borderRadius: 'var(--radius-md)',
                    color: isBurnVerified ? 'var(--text-muted)' : 'var(--text-primary)',
                    fontSize: '0.9375rem',
                    cursor: isBurnVerified ? 'not-allowed' : 'text'
                  }}
                  data-testid="input-burn-rate"
                />
              </div>
              {validationErrors.business_burn_rate_manual && (
                <span style={{ fontSize: '0.75rem', color: 'var(--status-error)', marginTop: '4px', display: 'block' }}>
                  {validationErrors.business_burn_rate_manual}
                </span>
              )}
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px', display: 'block' }}>
                {isBurnVerified 
                  ? 'Burn rate verified from finance data.' 
                  : 'Will be replaced automatically when finance data is verified.'
                }
              </span>
            </div>
          </div>

          {/* Save Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleSaveClick}
              disabled={saving || !hasChanges}
              style={{
                padding: '10px 24px',
                background: hasChanges ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: hasChanges ? 'white' : 'var(--text-muted)',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: (saving || !hasChanges) ? 'not-allowed' : 'pointer',
                opacity: (saving || !hasChanges) ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              data-testid="save-targets-btn"
            >
              {saving ? (
                <>
                  <Loader2 size={14} className="spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </GlassPanel>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        saving={saving}
      />
    </>
  );
}

export function BusinessReality() {
  const { token, orgId } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [stability, setStability] = useState(null);
  const [incomeInputs, setIncomeInputs] = useState(null);
  const [burnRateInfo, setBurnRateInfo] = useState(null);
  const [pricingIntel, setPricingIntel] = useState(null);
  const [whatWouldItTake, setWhatWouldItTake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scenariosExpanded, setScenariosExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token || !orgId) return;
    
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel including income inputs and burn rate
      const [snapshotRes, stabilityRes, incomeRes, burnRateRes, pricingRes, wwitRes] = await Promise.all([
        fetch(`${API_URL}/api/business-reality/${orgId}/snapshot`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-reality/${orgId}/stability`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-reality/${orgId}/income-inputs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-reality/${orgId}/burn-rate`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-reality/${orgId}/pricing-intelligence`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/business-reality/${orgId}/what-would-it-take`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (snapshotRes.ok) {
        setSnapshot(await snapshotRes.json());
      }
      if (stabilityRes.ok) {
        setStability(await stabilityRes.json());
      }
      if (incomeRes.ok) {
        const data = await incomeRes.json();
        setIncomeInputs(data.inputs);
      }
      if (burnRateRes.ok) {
        setBurnRateInfo(await burnRateRes.json());
      }
      if (pricingRes.ok) {
        setPricingIntel(await pricingRes.json());
      }
      if (wwitRes.ok) {
        setWhatWouldItTake(await wwitRes.json());
      }
    } catch (err) {
      setError('Unable to load business reality data.');
      console.error('Business reality fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token, orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle save income targets
  const handleSaveTargets = async (payload) => {
    if (!token || !orgId) return false;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/business-reality/${orgId}/income-inputs`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        // Refresh all data after successful save
        await fetchData();
        return true;
      } else {
        const err = await response.json();
        console.error('Save targets error:', err);
        return false;
      }
    } catch (err) {
      console.error('Save targets error:', err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Format number
  const formatNumber = (value, decimals = 1) => {
    if (value === null || value === undefined) return '—';
    return value.toFixed(decimals);
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header-content">
            <h1>Business Reality</h1>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" data-testid="business-reality-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Business Reality</h1>
          <p>A factual view of where your business stands</p>
        </div>
      </div>

      {error && (
        <GlassPanel className="mb-lg">
          <p style={{ color: 'var(--text-muted)' }}>{error}</p>
        </GlassPanel>
      )}

      {/* Section 1: Business Reality Snapshot */}
      <GlassPanel 
        className="mb-lg" 
        title="Business Reality Snapshot"
        titleIcon={Activity}
        headerAction={<BurnStateBadge burnRateInfo={snapshot?.burn_rate_info} />}
        data-testid="snapshot-section"
      >
        <div className="responsive-grid-3" style={{ 
          gap: 'var(--space-lg)',
          padding: 'var(--space-md) 0'
        }}>
          {/* Runway Months */}
          <div data-testid="runway-months-card">
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 'var(--space-xs)'
            }}>
              Runway
            </div>
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '600', 
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family-display)'
            }}>
              {snapshot?.runway?.runway_months 
                ? `${formatNumber(snapshot.runway.runway_months, 0)} months`
                : '—'
              }
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
              {snapshot?.runway?.message || 'Insufficient data'}
            </div>
          </div>

          {/* Monthly Burn */}
          <div data-testid="monthly-burn-card">
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 'var(--space-xs)'
            }}>
              Monthly Burn Rate
            </div>
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '600', 
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family-display)'
            }}>
              {formatCurrency(snapshot?.monthly_burn_rate)}
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
              Average monthly outflows
            </div>
          </div>

          {/* Income Gap */}
          <div data-testid="income-gap-card">
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 'var(--space-xs)'
            }}>
              Income Gap
            </div>
            <div style={{ 
              fontSize: '2rem', 
              fontWeight: '600', 
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family-display)'
            }}>
              {snapshot?.revenue_requirement?.revenue_gap_minimum 
                ? formatCurrency(snapshot.revenue_requirement.revenue_gap_minimum) + '/mo'
                : snapshot?.inputs_configured ? 'None' : '—'
              }
            </div>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: 'var(--space-xs)' }}>
              {snapshot?.revenue_requirement?.message || 
               (snapshot?.inputs_configured 
                 ? 'Based on your income targets' 
                 : 'Set income targets to calculate'
               )
              }
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* Section 2: Stability Buffer Card (Phase 1D) */}
      <StabilityBufferCard stability={stability} />

      {/* Section 3: Income & Burn Targets Configuration (Phase 1E) */}
      <IncomeTargetsConfig
        incomeInputs={incomeInputs}
        burnRateInfo={burnRateInfo || snapshot?.burn_rate_info}
        onSave={handleSaveTargets}
        saving={saving}
      />

      {/* Section 4: Runway Indicator */}
      <GlassPanel className="mb-lg" data-testid="runway-indicator-section">
        <div style={{ padding: 'var(--space-md) 0' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-md)'
          }}>
            <Activity size={18} style={{ color: 'var(--text-muted)' }} />
            <span style={{ 
              fontSize: '0.875rem', 
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              Runway Indicator
            </span>
          </div>

          {/* Simple visual bar */}
          {snapshot?.runway?.runway_months !== null && snapshot?.runway?.runway_months !== undefined && (
            <div style={{ marginBottom: 'var(--space-lg)' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-md)',
                marginBottom: 'var(--space-sm)'
              }}>
                <div style={{
                  flex: 1,
                  height: '8px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div 
                    style={{
                      width: `${Math.min((snapshot.runway.runway_months / 12) * 100, 100)}%`,
                      height: '100%',
                      background: 'var(--text-muted)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease'
                    }}
                    data-testid="runway-bar"
                  />
                </div>
                <span style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--text-muted)',
                  minWidth: '80px',
                  textAlign: 'right'
                }}>
                  {formatNumber(snapshot.runway.runway_months, 1)} months
                </span>
              </div>
            </div>
          )}

          {/* 6-month buffer reference */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-sm)',
            padding: 'var(--space-md)',
            background: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-primary)'
          }}>
            <Info size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: 'var(--space-xs)' }}>
                6-Month Buffer Reference
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {snapshot?.runway?.six_month_buffer_target 
                  ? `A 6-month buffer at your current burn rate would be ${formatCurrency(snapshot.runway.six_month_buffer_target)}. This is informational only.`
                  : 'Insufficient burn rate data to calculate buffer target.'
                }
              </div>
            </div>
          </div>
        </div>
      </GlassPanel>

      {/* Section 3: What Would It Take? (Collapsed by default) */}
      <GlassPanel className="mb-lg" data-testid="what-would-it-take-section">
        <button
          onClick={() => setScenariosExpanded(!scenariosExpanded)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: 'var(--space-md) 0',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-primary)',
            textAlign: 'left'
          }}
          data-testid="expand-scenarios-btn"
        >
          <div>
            <div style={{ 
              fontSize: '0.875rem', 
              fontWeight: '500',
              marginBottom: 'var(--space-xs)'
            }}>
              What would it take?
            </div>
            <div style={{ 
              fontSize: '0.8125rem', 
              color: 'var(--text-muted)',
              fontWeight: '400'
            }}>
              Based on your completed jobs and connected financial data.
            </div>
          </div>
          {scenariosExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {scenariosExpanded && (
          <div style={{ paddingBottom: 'var(--space-md)' }} data-testid="scenarios-content">
            {/* Job Volume Scenario */}
            {whatWouldItTake?.job_volume_scenario && (
              <div style={{ 
                marginBottom: 'var(--space-lg)',
                padding: 'var(--space-md)',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)'
              }} data-testid="job-volume-scenario">
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 'var(--space-md)'
                }}>
                  Job Volume Required
                </div>
                <div className="responsive-grid-3" style={{ 
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-sm)'
                }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Average Revenue/Job</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {formatCurrency(whatWouldItTake.job_volume_scenario.average_job_revenue)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Average Margin</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {formatNumber(whatWouldItTake.job_volume_scenario.average_job_margin_pct)}%
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Additional Jobs/Month</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {whatWouldItTake.job_volume_scenario.additional_jobs_needed 
                        ? `+${formatNumber(whatWouldItTake.job_volume_scenario.additional_jobs_needed)}`
                        : '—'
                      }
                    </div>
                  </div>
                </div>
                <div style={{ 
                  fontSize: '0.8125rem', 
                  color: 'var(--text-muted)', 
                  fontStyle: 'italic'
                }}>
                  {whatWouldItTake.job_volume_scenario.note}
                </div>
              </div>
            )}

            {/* Margin Improvement Scenario */}
            {whatWouldItTake?.margin_improvement_scenario && (
              <div style={{ 
                marginBottom: 'var(--space-lg)',
                padding: 'var(--space-md)',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)'
              }} data-testid="margin-scenario">
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 'var(--space-md)'
                }}>
                  Margin Increase Required
                </div>
                <div className="responsive-grid-3" style={{ 
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-sm)'
                }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Current Margin</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {formatNumber(whatWouldItTake.margin_improvement_scenario.current_average_margin_pct)}%
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Target Margin</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {whatWouldItTake.margin_improvement_scenario.target_margin_pct 
                        ? `${formatNumber(whatWouldItTake.margin_improvement_scenario.target_margin_pct)}%`
                        : '—'
                      }
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Increase Needed</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {whatWouldItTake.margin_improvement_scenario.margin_increase_needed 
                        ? `+${formatNumber(whatWouldItTake.margin_improvement_scenario.margin_increase_needed)} pts`
                        : '—'
                      }
                    </div>
                  </div>
                </div>
                <div style={{ 
                  fontSize: '0.8125rem', 
                  color: 'var(--text-muted)', 
                  fontStyle: 'italic'
                }}>
                  {whatWouldItTake.margin_improvement_scenario.note}
                </div>
              </div>
            )}

            {/* Cost Reduction Scenario */}
            {whatWouldItTake?.cost_reduction_scenario && whatWouldItTake.cost_reduction_scenario.cost_reduction_needed && (
              <div style={{ 
                marginBottom: 'var(--space-lg)',
                padding: 'var(--space-md)',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)'
              }} data-testid="cost-scenario">
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--text-muted)', 
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 'var(--space-md)'
                }}>
                  Cost Reduction Required
                </div>
                <div className="responsive-grid-3" style={{ 
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-sm)'
                }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Current Monthly Costs</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {formatCurrency(whatWouldItTake.cost_reduction_scenario.current_monthly_costs)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Reduction Needed</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {formatCurrency(whatWouldItTake.cost_reduction_scenario.cost_reduction_needed)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>As Percentage</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                      {formatNumber(whatWouldItTake.cost_reduction_scenario.cost_reduction_percentage)}%
                    </div>
                  </div>
                </div>
                <div style={{ 
                  fontSize: '0.8125rem', 
                  color: 'var(--text-muted)', 
                  fontStyle: 'italic'
                }}>
                  {whatWouldItTake.cost_reduction_scenario.note}
                </div>
              </div>
            )}

            {/* No scenarios available */}
            {!whatWouldItTake?.job_volume_scenario && 
             !whatWouldItTake?.margin_improvement_scenario && (
              <div style={{ 
                textAlign: 'center', 
                padding: 'var(--space-lg)',
                color: 'var(--text-muted)'
              }}>
                {whatWouldItTake?.income_gap?.target_monthly_income 
                  ? 'No income gap detected based on current data.'
                  : 'Set your income targets in account settings to use this calculator.'
                }
              </div>
            )}

            {/* Footer disclaimer */}
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              textAlign: 'center',
              paddingTop: 'var(--space-md)',
              borderTop: '1px solid var(--border-primary)',
              fontStyle: 'italic'
            }}>
              These scenarios are illustrative and based on your real job history and financial data.
            </div>
          </div>
        )}
      </GlassPanel>

      {/* Section 4: Evidence Footer */}
      <GlassPanel data-testid="evidence-footer">
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-md)',
          padding: 'var(--space-sm) 0'
        }}>
          <Database size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 'var(--space-xs)'
            }}>
              Data Sources
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              {snapshot?.data_sources?.length > 0 
                ? `This analysis uses data from: ${snapshot.data_sources.join(', ')}.`
                : 'No data sources available.'
              }
              {snapshot?.data_gaps?.length > 0 && (
                <span style={{ display: 'block', marginTop: 'var(--space-xs)' }}>
                  Data gaps: {snapshot.data_gaps.join('; ')}.
                </span>
              )}
            </div>
            <div style={{ 
              fontSize: '0.75rem', 
              color: 'var(--text-muted)', 
              marginTop: 'var(--space-sm)',
              opacity: 0.7
            }}>
              Last calculated: {snapshot?.calculated_at 
                ? new Date(snapshot.calculated_at).toLocaleString('en-GB')
                : '—'
              }
            </div>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}

export default BusinessReality;
